
/**
 * `MediaGallery` - The Media Gallery module for PI is a great way to illustrate the page with images in a dynamic grid layout.
 *
 *  This component has a specific requirement that a row height must be the same of the width of 8 columns, and columns
 *  do not have a fixed width. As such, this value must be computed on runtime.
 *  A resize observer is used, as it does not cause layout thrashing (as opposed to getClientRect() and the likes)
 *  and a css custom property is set "--mediaGalleryWidth". While webpack could be leveraged to export the css
 *  values into the javascript code, this would not happen at runtime, and gutters are a variable here that makes
 *  having all the computation done by calc the preferred option.
 */
import ComponentInitializeDispatcher from '@owc-utils/Mixins/ComponentInitializeDispatcher';
import ValidationBoxWrapper from '@owc-packages/ValidationBox/src';
import SlotContainer from '@owc-packages/SlotContainer/src/SlotContainer.vue';
import {
  MEDIA_GALLERY_DEBOUNCE_TIME,
  MEDIA_GALLERY_ITEM_ACTION_EVENT_NAME,
  MEDIA_GALLERY_ITEM_TRACKING_EVENT_NAME,
} from './mediaGallery.config';
import { debounce } from '@owc-utils/Events';
import MediaGalleryOverlay from './MediaGalleryOverlay/MediaGalleryOverlay.vue';
import { getComponentStateConnection, subscribeComponentState } from '@owc-utils/Store/owcComponent';
import {
  MediaGalleryData,
  MediaGalleryMethods,
  MediaGalleryComputed,
  Payload,
  MediaGalleryItemComponent,
} from './MediaGallery';
import { Props, State } from '@/packages/_types/ComponentInitializeDispatcher';
import { parseTree, Tree } from './MediaGalleryUtils';
import { getLocalFootnotes, setModuleId } from '@owc-utils/Footnotes/FootnotesHelper';

let isItemHeightSet = false;

export default ComponentInitializeDispatcher.extend<
  MediaGalleryData,
  MediaGalleryMethods,
  MediaGalleryComputed,
  Partial<Props>
>({
  name: 'owc-media-gallery',
  components: {
    SlotContainer,
    MediaGalleryOverlay,
    ValidationBoxWrapper,
  },
  async mounted() {
    const id = this.getComponentId();

    this.resizeEventHandler();
    if (this.isBuildWc) {
      this.$nextTick(() => {
        const tree: Tree = parseTree(this.getHost());
        const listener = (state: State): void => {
          const innerItems: string[] = [];
          const items = tree.map(({ childComponents, id: itemId }) => {
            const item = state.find(component => component.id === itemId);
            const inner = childComponents?.[0];
            const found = state.find(component => component.id === inner?.id);
            if (found) {
              innerItems.push(found.id);
            }
            return {
              itemId,
              itemJson: item?.json || {},
              id: found?.id,
              json: found?.json,
            } as MediaGalleryItemComponent;
          });
          this.currentGalleryItems = items;
          this.setItemHeight(innerItems);
        };
        subscribeComponentState(id, listener);
      });
    }
    this.resizeEventHandler();

    const resizeObserver = new ResizeObserver(
      debounce(MEDIA_GALLERY_DEBOUNCE_TIME, this.resizeEventHandler, false),
    );

    resizeObserver.observe(this.$refs.mediaGalleryContainer as HTMLElement);
    this.$el.addEventListener(MEDIA_GALLERY_ITEM_ACTION_EVENT_NAME, this.openOverlay as EventListener);
    this.$el.addEventListener(MEDIA_GALLERY_ITEM_TRACKING_EVENT_NAME, this.handleTracking as EventListener);
  },
  data() {
    return {
      isOverlayVisible: false,
      currentGalleryItems: [],
      currentGalleryItemIndex: 0,
      currentGridWidth: 0,
      isTrackingElementDispached: false,
    };
  },
  computed: {
    layoutClass: function() {
      const count = this.componentData?.totalItems;
      return `owc-media-gallery__${count}-items`;
    },
    isDarkMode: function() {
      return Boolean(this.componentData?.darkMode);
    },
    pinchLabel: function() {
      return this.componentData?.labels?.overlayPinchLabel || '';
    },
    footnotes() {
      return getLocalFootnotes(this.componentData);
    },
    moduleId() {
      return setModuleId(this);
    },
  },
  methods: {
    async setItemHeight(tree: string[]) {
      const payload: Payload[] = [];
      if (!isItemHeightSet) {
        tree.forEach(id => {
          payload.push({
            id,
            json: {
              height: 'var(--mediaGalleryRowHeight)',
            },
          });
        });
        const connection = await getComponentStateConnection();
        isItemHeightSet = true;
        connection.updateComponents(payload);
      }
    },
    resizeEventHandler: function(): void {
      const mediaGalleryElement = this.$el as HTMLElement;
      const mediaGalleryContentWidth = mediaGalleryElement.clientWidth;

      // since for webkit 100vh is not the exact value as availble viewport
      // (the browser bar takes space from here) we'd need to use -webkit-fill-available
      // to get the exact value for 100vh. Still, this is not compatible with calc()
      // to work around this issue, we set a custom property for innerHeight that can then
      // be used for correct values in webkit
      mediaGalleryElement.style.setProperty('--window-height', `${window.innerHeight}px`);

      if (this.currentGridWidth !== mediaGalleryContentWidth) {
        mediaGalleryElement.style.setProperty('--mediaGalleryWidth', `${mediaGalleryContentWidth}px`);
      }
      this.currentGridWidth = mediaGalleryContentWidth;
    },
    openOverlay(screenMaxEvent: CustomEvent): void {
      this.isOverlayVisible = true;
      this.currentGalleryItemIndex = this.currentGalleryItems.findIndex(
        item => item.itemId === screenMaxEvent.detail.itemId,
      );
    },
    handleTracking(trackingEvent: CustomEvent): void {
      if (!this.isTrackingElementDispached) {
        for (let i = 0; i < this.currentGalleryItems.length; i++) {
          if (this.currentGalleryItems[i].itemId === trackingEvent.detail.itemId) {
            const mediaGalleryItem = this.currentGalleryItems[i];

            const trackingIndex = i + 1;
            const trackingContentType = mediaGalleryItem.json.videoDetails ? 'video' : 'image';
            const trackingButtonType = trackingEvent.detail.buttonType;

            const trackingLabel = `index${trackingIndex}.${trackingContentType}.${trackingButtonType}`;

            this.handleCustomTracking(this.$el, trackingLabel, 'feature', 'click');
            this.isTrackingElementDispached = true;
          }
        }
      }
    },
  },
});
